import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { useLanguage } from '../../context/LanguageContext';
import styles from './Login.module.css';
import { getApiUrl } from '../../api';

const Login = ({ onClose, onAuth }) => {
  const { t } = useLanguage();
  const [viewMode, setViewMode] = useState('login'); // 'login' | 'signup' | 'forgot'
  const [email, setEmail] = useState('khurram@panacloud.com');
  const [password, setPassword] = useState('•••••');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [error, setError] = useState('');

  const switchToLogin = () => {
    setViewMode('login');
    setForgotSent(false);
    setSuccessMsg('');
    setError('');
  };

  const switchToSignup = () => {
    setViewMode('signup');
    setForgotSent(false);
    setSuccessMsg('');
    setError('');
  };

  const switchToForgot = (e) => {
    if (e) e.preventDefault();
    setViewMode('forgot');
    setForgotSent(false);
    setSuccessMsg('');
    setError('');
    if (email === 'khurram@panacloud.com') setEmail('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    const endpoint = viewMode === 'signup' ? '/api/auth/signup' : '/api/auth/login';
    try {
      const res = await fetch(getApiUrl(endpoint), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });

      const text = await res.text();
      let data = {};
      try { data = JSON.parse(text); } catch { data = { message: text || 'Request failed' }; }

      if (!res.ok) {
        const errMsg = data.message || 
          (data.errors && data.errors.map(e => e.msg).join(', ')) || 
          (viewMode === 'signup' ? 'Signup failed' : 'Login failed');
        setError(errMsg);
        toast.error(errMsg);
        setIsLoading(false);
        return;
      }

      if (data.token) {
        localStorage.setItem('token1', data.token);
      }
      if (data.user) {
        localStorage.setItem('user1', JSON.stringify(data.user));
      }
      toast.success(viewMode === 'signup' ? 'Account created successfully!' : 'Welcome back!');
      // Post-auth navigation is the caller's responsibility (see HomePage's
      // handleAuthSuccess) - it knows whether to send the user back to a
      // protected page they were bounced from, or to the default dashboard.
      if (onAuth) onAuth();
      if (onClose) onClose();
    } catch (err) {
      console.error(err);
      const errMsg = err.message || (viewMode === 'signup' ? 'Signup error' : 'Login error');
      setError(errMsg);
      toast.error(errMsg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!email) {
      setError('Please enter your email address');
      toast.error('Please enter your email address');
      return;
    }
    setIsLoading(true);
    setError('');
    try {
      const res = await fetch(getApiUrl('/api/auth/forgot-password'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });

      const text = await res.text();
      let data = {};
      try { data = JSON.parse(text); } catch { data = { message: text || 'Request failed' }; }

      if (!res.ok) {
        const errMsg = data.message || (data.errors && data.errors.map(e => e.msg).join(', ')) || 'Failed to send reset link';
        setError(errMsg);
        toast.error(errMsg);
        setIsLoading(false);
        return;
      }
      const msg = data.message || 'Password reset link sent to your email!';
      setSuccessMsg(msg);
      setForgotSent(true);
      toast.success(msg);
    } catch (err) {
      console.error(err);
      const errMsg = err.message || 'Error sending reset link';
      setError(errMsg);
      toast.error(errMsg);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="d-flex justify-content-center w-100">
      <div className={`card shadow-sm border-0 ${styles.authCard} position-relative`}>
        {onClose && (
          <button type="button" className={`btn-close ${styles.authClose}`} onClick={onClose} aria-label="Close" />
        )}

        <div className="card-body p-4">
          <div className={`${styles.authLogo} mb-3`}>
            <svg width="30" height="30" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="40" cy="40" r="40" fill="#E0E7FF" />
              <path d="M35 25C35 25 30 30 30 35C30 45 35 50 40 50C45 50 50 45 50 35C50 30 45 25 40 25M40 25V50M50 35H30" stroke="#6B5FFF" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </div>

          {error && (
            <div className="alert alert-danger small mb-3 text-center" role="alert">
              {error}
            </div>
          )}

          {viewMode === 'forgot' ? (
            <div>
              <h5 className="card-title text-center mb-2">
                {t('forgotPasswordTitle', 'Reset Password')}
              </h5>
              <p className="text-muted small text-center mb-4">
                {t('forgotPasswordSub', "Enter your account's email address and we'll send you a password reset link (valid for 15 minutes).")}
              </p>

              {forgotSent ? (
                <div className="text-center py-3">
                  <div className="alert alert-success small mb-3">
                    {successMsg || t('resetEmailSent', 'A reset link has been sent to your email address. Please check your inbox.')}
                  </div>
                  <button
                    type="button"
                    className="btn btn-primary btn-sm w-100"
                    onClick={switchToLogin}
                  >
                    {t('backToLogin', 'Back to Login')}
                  </button>
                </div>
              ) : (
                <form onSubmit={handleForgotPassword}>
                  <div className="mb-3">
                    <label htmlFor="forgot-email" className="form-label">{t('emailLabel', 'Email')}</label>
                    <input
                      type="email"
                      id="forgot-email"
                      className="form-control"
                      value={email}
                      onChange={(e) => { setEmail(e.target.value); setError(''); }}
                      placeholder="name@example.com"
                      required
                    />
                  </div>

                  <div className="d-grid mt-4">
                    <button type="submit" className="btn btn-primary" disabled={isLoading}>
                      {isLoading ? t('sending', 'Sending...') : t('sendResetLink', 'Send Reset Link')}
                    </button>
                  </div>

                  <div className="text-center mt-3">
                    <button
                      type="button"
                      className="btn btn-link link-primary p-0 small text-decoration-none"
                      onClick={switchToLogin}
                      style={{ fontSize: '0.85rem' }}
                    >
                      {t('alreadyHaveAccount', 'Remembered your password? Login')}
                    </button>
                  </div>
                </form>
              )}
            </div>
          ) : (
            <div>
              <h5 className="card-title text-center mb-4">
                {viewMode === 'signup' ? t('createAccount', 'Create your account') : t('welcomeBack', 'Login to ReplyShoot')}
              </h5>

              <form onSubmit={handleSubmit}>
                <div className="mb-3">
                  <label htmlFor="email" className="form-label">{t('emailLabel', 'Email')}</label>
                  <input
                    type="email"
                    id="email"
                    className="form-control"
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); setError(''); }}
                    required
                  />
                </div>

                <div className="mb-3 position-relative">
                  <label htmlFor="password" className="form-label">{t('passwordLabel', 'Password')}</label>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    className="form-control pe-5"
                    value={password}
                    onChange={(e) => { setPassword(e.target.value); setError(''); }}
                    required
                  />
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary position-absolute top-50 end-0 translate-middle-y me-2"
                    style={{ zIndex: 5 }}
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? t('hide', 'Hide') : t('show', 'Show')}
                  </button>
                </div>

                <div className="d-grid mt-4">
                  <button type="submit" className="btn btn-primary" disabled={isLoading}>
                    {isLoading
                      ? t('pleaseWait', 'Please wait...')
                      : viewMode === 'signup'
                      ? t('signUp', 'Sign up & Log in')
                      : t('login', 'Login')}
                  </button>
                </div>
              </form>

              <div className="text-center mt-3 d-flex flex-column align-items-center gap-2">
                {viewMode === 'login' && (
                  <button
                    type="button"
                    className="btn btn-link link-secondary p-0 small text-decoration-none"
                    onClick={switchToForgot}
                  >
                    {t('forgotPassword', 'Forgot password?')}
                  </button>
                )}
                <button
                  type="button"
                  className="btn btn-link link-primary p-0 small text-decoration-none"
                  onClick={viewMode === 'signup' ? switchToLogin : switchToSignup}
                  style={{ fontSize: '0.85rem' }}
                >
                  {viewMode === 'signup'
                    ? t('alreadyHaveAccount', 'Already have an account? Login')
                    : t('dontHaveAccount', "Don't have an account? Quick Sign Up")}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Login;
